import LeadName from '@/components/Lead/LeadName';
import { CaratPlanningModel } from '@/typings/models/CaratModel';
import { LeadDetailsModel } from '@/typings/models/Lead';
import { F } from '@/utils/intl';
import {
  Alert,
  Col,
  Collapse,
  Descriptions,
  Empty,
  Flex,
  Row,
  Skeleton,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import React from 'react';
import { useIntl } from 'umi';
import CaratPlanPositions from './CaratPlanPositions';
import styles from './CaratPlanViewer.less';
import { useCaratAttributeColumns, useCaratGroupColumns } from './constants';
import {
  CARAT_EMPTY,
  CARAT_TRADE_GROUP_COLORS,
  useCaratFormat,
} from './format';
import { useCaratPlanningDetail } from './useCaratPlanningDetail';

const { Paragraph, Text } = Typography;

export interface CaratPlanViewerProps {
  plannings: CaratPlanningModel[];
  leadDetails?: LeadDetailsModel;
  loading?: boolean;
  /** Planning to open first; defaults to the newest one. */
  selectedPlanning?: CaratPlanningModel;
}

/**
 * One planning of a Carat export. Carat pushes a whole `.DAT` on every change,
 * so everything below is what that export said at the time it was received — it
 * is never edited here.
 */
const CaratSinglePlanViewer: React.FC<{
  planning: CaratPlanningModel;
  leadDetails?: LeadDetailsModel;
}> = ({ planning, leadDetails }) => {
  const intl = useIntl();
  const { detail, loading } = useCaratPlanningDetail(planning.id);

  const parsed = detail?.parsed;
  const details = parsed?.details;
  const quotation = details?.quotation;
  const format = useCaratFormat(quotation?.currency);
  const groupColumns = useCaratGroupColumns(format);
  const attributeColumns = useCaratAttributeColumns();

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />;

  const label = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  /**
   * A planning ingested before the detail extraction existed only carries the
   * matched fields. Saying so beats rendering a page of empty sections.
   */
  if (!details) {
    return (
      <Flex vertical gap={16} className={styles.plan}>
        <Alert
          type="info"
          showIcon
          title={
            <F
              id="pages.integrations.carat.no_details"
              defaultMessage="This planning was received before the full export was read. Its detail appears the next time Carat sends it."
            />
          }
        />
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item
            label={label(
              'pages.integrations.carat.commission_id',
              'Commission ID',
            )}
          >
            {format.text(planning.commission_id)}
          </Descriptions.Item>
          <Descriptions.Item
            label={label('pages.lead.fields.order_volume', 'Order Volume')}
          >
            {format.text(planning.deal_value)}
          </Descriptions.Item>
          <Descriptions.Item
            label={label('pages.integrations.carat.agent', 'Carat agent')}
          >
            {[planning.agent_short_sign, planning.agent_name]
              .filter(Boolean)
              .join(' · ') || CARAT_EMPTY}
          </Descriptions.Item>
          <Descriptions.Item
            label={label('pages.integrations.carat.received_at', 'Received at')}
          >
            {format.dateTime(planning.received_at)}
          </Descriptions.Item>
        </Descriptions>
      </Flex>
    );
  }

  const catalogs = details.catalogs ?? [];
  const positionCount = details.positions?.length ?? 0;

  const statistics = [
    {
      key: 'gross',
      title: label('pages.integrations.carat.total_gross', 'Total (gross)'),
      value: format.money(quotation?.total_gross),
    },
    {
      key: 'net',
      title: label('pages.integrations.carat.total_net', 'Total (net)'),
      value: format.money(quotation?.total_net),
    },
    {
      key: 'discount',
      title: label('pages.billings.discount', 'Discount'),
      value:
        quotation?.discount == null
          ? CARAT_EMPTY
          : `${format.money(quotation.discount)} (${format.percent(
              quotation.discount_percent,
            )})`,
    },
    {
      key: 'offered',
      title: label('pages.integrations.carat.offered_on', 'Offered on'),
      value: format.date(quotation?.offered_on),
    },
  ];

  const overview = (
    <Descriptions bordered size="small" column={{ xs: 1, xl: 2 }}>
      <Descriptions.Item
        label={label('pages.integrations.carat.commission_id', 'Commission ID')}
      >
        {format.text(planning.commission_id)}
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.variation', 'Variation')}
      >
        {format.text(planning.variation)}
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.commission_text', 'Commission')}
      >
        {format.text(quotation?.commission_text)}
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.programme', 'Programme')}
      >
        {format.text(quotation?.programme)}
      </Descriptions.Item>
      <Descriptions.Item label={label('pages.integrations.carat.room', 'Room')}>
        {[details.plan?.name, details.plan?.room_number]
          .filter(Boolean)
          .join(' · ') || CARAT_EMPTY}
      </Descriptions.Item>
      <Descriptions.Item
        label={label(
          'pages.integrations.carat.working_height',
          'Working height',
        )}
      >
        {format.text(details.plan?.working_height)}
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.agent', 'Carat agent')}
      >
        <Flex gap={4} wrap>
          {(details.agents ?? []).map((agent) => (
            <Tag key={`${agent.short_sign}-${agent.touched_at}`}>
              {[agent.name, agent.short_sign].filter(Boolean).join(' · ')}
            </Tag>
          ))}
          {(details.agents?.length ?? 0) === 0 && CARAT_EMPTY}
        </Flex>
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.catalog', 'Catalogue')}
      >
        <Flex gap={4} wrap>
          {catalogs.map((catalog) => (
            <Tag
              key={catalog.id}
              color={CARAT_TRADE_GROUP_COLORS[catalog.trade_group ?? '']}
            >
              {catalog.name}
            </Tag>
          ))}
          {catalogs.length === 0 && CARAT_EMPTY}
        </Flex>
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.created_on', 'Created on')}
      >
        {format.date(quotation?.created_on)}
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.delivery_term', 'Delivery term')}
      >
        {format.text(quotation?.delivery_term)}
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.positions', 'Positions')}
      >
        {positionCount}
      </Descriptions.Item>
      <Descriptions.Item
        label={label('pages.integrations.carat.received_at', 'Received at')}
      >
        {format.dateTime(planning.received_at)}
      </Descriptions.Item>
    </Descriptions>
  );

  const configuration = (
    <Flex vertical gap={16}>
      <Paragraph type="secondary" className={styles.hint}>
        <F
          id="pages.integrations.carat.configuration_hint"
          defaultMessage="The options the kitchen was configured with in Carat — fronts, carcase, handles, worktop and niche."
        />
      </Paragraph>
      {(details.configuration?.length ?? 0) > 0 ? (
        <Table
          size="small"
          rowKey={(attribute) => `${attribute.code}-${attribute.value_code}`}
          dataSource={details.configuration}
          columns={attributeColumns}
          pagination={false}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Flex>
  );

  const pricing = (
    <Flex vertical gap={16}>
      <Table
        size="small"
        rowKey={(group) => `${group.trade_group}-${group.number}`}
        dataSource={details.groups}
        columns={groupColumns}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      />

      <Descriptions bordered size="small" column={{ xs: 1, xl: 2 }}>
        <Descriptions.Item
          label={label('pages.integrations.carat.list_price', 'List price')}
        >
          {format.money(quotation?.list_gross)}
        </Descriptions.Item>
        <Descriptions.Item label={label('pages.billings.discount', 'Discount')}>
          {format.signedMoney(quotation?.discount)}
          {quotation?.discount_percent != null &&
            ` (${format.percent(quotation.discount_percent)})`}
        </Descriptions.Item>
        <Descriptions.Item
          label={label('pages.integrations.carat.total_gross', 'Total (gross)')}
        >
          {format.money(quotation?.total_gross)}
        </Descriptions.Item>
        <Descriptions.Item label={label('pages.integrations.carat.vat', 'VAT')}>
          {format.money(quotation?.total_vat)}
          {quotation?.vat_rate != null &&
            ` (${format.percent(quotation.vat_rate)})`}
        </Descriptions.Item>
        <Descriptions.Item
          label={label('pages.integrations.carat.total_net', 'Total (net)')}
        >
          {format.money(quotation?.total_net)}
        </Descriptions.Item>
        <Descriptions.Item
          label={label('pages.integrations.carat.currency', 'Currency')}
        >
          {format.text(quotation?.currency)}
        </Descriptions.Item>
      </Descriptions>

      <Alert
        type="warning"
        showIcon
        title={
          <F
            id="pages.integrations.carat.internal_pricing"
            defaultMessage="Purchase cost and margin are Carat's internal figures — never quote them to a customer."
          />
        }
        description={
          <Descriptions size="small" column={{ xs: 1, md: 2 }} colon={false}>
            <Descriptions.Item
              label={label('pages.integrations.carat.cost', 'Purchase cost')}
            >
              {format.money(quotation?.total_cost)}
            </Descriptions.Item>
            <Descriptions.Item
              label={label('pages.integrations.carat.margin', 'Margin')}
            >
              {format.money(quotation?.margin)}
            </Descriptions.Item>
          </Descriptions>
        }
      />
    </Flex>
  );

  const catalogsTab = (
    <Flex vertical gap={16}>
      {catalogs.map((catalog) => (
        <Descriptions
          key={catalog.id}
          bordered
          size="small"
          column={{ xs: 1, xl: 2 }}
          title={
            <Flex align="center" gap={8}>
              {catalog.trade_group && (
                <Tag color={CARAT_TRADE_GROUP_COLORS[catalog.trade_group]}>
                  {catalog.trade_group}
                </Tag>
              )}
              <Text strong>{catalog.name}</Text>
            </Flex>
          }
        >
          <Descriptions.Item
            label={label(
              'pages.integrations.carat.manufacturer',
              'Manufacturer',
            )}
          >
            {[catalog.manufacturer?.brand, catalog.manufacturer?.company]
              .filter(Boolean)
              .join(' · ') || CARAT_EMPTY}
          </Descriptions.Item>
          <Descriptions.Item
            label={label('pages.integrations.carat.series', 'Series')}
          >
            {(catalog.series ?? []).map((series) => series.name).join(', ') ||
              CARAT_EMPTY}
          </Descriptions.Item>
          <Descriptions.Item
            label={label('pages.integrations.carat.price_list', 'Price list')}
          >
            {[catalog.price_list, catalog.carat_version]
              .filter(Boolean)
              .join(' · ') || CARAT_EMPTY}
            {catalog.price_list_on &&
              ` (${format.date(catalog.price_list_on)})`}
          </Descriptions.Item>
          <Descriptions.Item
            label={label('pages.integrations.carat.delivery', 'Delivery')}
          >
            {[
              catalog.delivery?.term,
              catalog.delivery?.week,
              catalog.delivery?.on ? format.date(catalog.delivery.on) : null,
            ]
              .filter(Boolean)
              .join(' · ') || CARAT_EMPTY}
          </Descriptions.Item>
          <Descriptions.Item
            label={label('pages.integrations.carat.order_number', 'Order no.')}
          >
            {catalog.order?.number
              ? `${catalog.order.number}${
                  catalog.order.ordered_on
                    ? ` (${format.date(catalog.order.ordered_on)})`
                    : ''
                }`
              : CARAT_EMPTY}
          </Descriptions.Item>
          <Descriptions.Item
            label={label(
              'pages.integrations.carat.confirmed_on',
              'Confirmed on',
            )}
          >
            {format.date(catalog.order?.confirmed_on)}
          </Descriptions.Item>
        </Descriptions>
      ))}
      {catalogs.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </Flex>
  );

  const customer = (
    <Flex vertical gap={16}>
      <Descriptions
        bordered
        size="small"
        column={1}
        title={<F id="entity.customer.singular" defaultMessage="Customer" />}
      >
        <Descriptions.Item label={label('common.attribute.name', 'Name')}>
          {leadDetails ? <LeadName lead={leadDetails} readonly /> : CARAT_EMPTY}
        </Descriptions.Item>
        <Descriptions.Item label={label('common.attribute.email', 'Email')}>
          {format.text(leadDetails?.customer?.email)}
        </Descriptions.Item>
        <Descriptions.Item label={label('common.attribute.phone', 'Phone')}>
          {format.text(leadDetails?.customer?.phone)}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        bordered
        size="small"
        column={1}
        title={
          <F
            id="pages.integrations.carat.carat_contact"
            defaultMessage="As delivered by Carat"
          />
        }
      >
        <Descriptions.Item label={label('common.attribute.name', 'Name')}>
          {[parsed?.firstname, parsed?.lastname].filter(Boolean).join(' ') ||
            CARAT_EMPTY}
        </Descriptions.Item>
        <Descriptions.Item label={label('customer.salutation', 'Salutation')}>
          {format.text(details.address?.salutation)}
        </Descriptions.Item>
        <Descriptions.Item label={label('common.attribute.email', 'Email')}>
          {format.text(parsed?.email)}
        </Descriptions.Item>
        <Descriptions.Item label={label('common.attribute.phone', 'Phone')}>
          {(parsed?.phones ?? []).join(' · ') || CARAT_EMPTY}
        </Descriptions.Item>
        <Descriptions.Item label={label('common.attribute.address', 'Address')}>
          {[
            details.address?.street,
            [details.address?.zip, details.address?.city]
              .filter(Boolean)
              .join(' '),
          ]
            .filter(Boolean)
            .join(', ') || CARAT_EMPTY}
        </Descriptions.Item>
        <Descriptions.Item
          label={label(
            'pages.integrations.carat.customer_number',
            'Carat customer number',
          )}
        >
          {format.text(
            parsed?.customer_number ?? details.address?.customer_number,
          )}
        </Descriptions.Item>
      </Descriptions>
    </Flex>
  );

  const technical = (
    <Flex vertical gap={16}>
      <Descriptions bordered size="small" column={{ xs: 1, xl: 2 }}>
        <Descriptions.Item
          label={label(
            'pages.integrations.carat.program_version',
            'Carat version',
          )}
        >
          {format.text(details.export?.program_version)}
        </Descriptions.Item>
        <Descriptions.Item
          label={label('pages.integrations.carat.exported_at', 'Exported at')}
        >
          {format.dateTime(details.export?.exported_at)}
        </Descriptions.Item>
        <Descriptions.Item
          label={label(
            'pages.integrations.carat.dealer_number',
            'Dealer number',
          )}
        >
          {format.text(details.export?.dealer_number)}
        </Descriptions.Item>
        <Descriptions.Item
          label={label('pages.integrations.carat.man_id', 'Man ID')}
        >
          {format.text(planning.man_id)}
        </Descriptions.Item>
      </Descriptions>

      {(details.checklist?.length ?? 0) > 0 && (
        <Descriptions
          bordered
          size="small"
          column={1}
          title={
            <F
              id="pages.integrations.carat.checklist"
              defaultMessage="Planner's notes"
            />
          }
        >
          {details.checklist?.map((entry, index) => (
            <Descriptions.Item
              key={`${entry.label}-${index}`}
              label={entry.label}
            >
              {format.text(entry.value)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {(details.documents?.length ?? 0) > 0 && (
        <Descriptions
          bordered
          size="small"
          column={1}
          title={
            <F
              id="pages.integrations.carat.documents"
              defaultMessage="Files Carat exported"
            />
          }
        >
          {details.documents?.map((document, index) => (
            <Descriptions.Item
              key={`${document.filename}-${index}`}
              label={document.label}
            >
              <Text code>{document.filename}</Text>
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      <Collapse
        size="small"
        items={[
          {
            key: 'raw',
            label: (
              <F
                id="pages.integrations.carat.raw_payload"
                defaultMessage="Raw Carat export"
              />
            ),
            children: (
              <pre className={styles.rawPayload}>{detail?.raw_payload}</pre>
            ),
          },
        ]}
      />
    </Flex>
  );

  return (
    <Flex vertical gap={16} className={styles.plan}>
      <Row gutter={[16, 16]}>
        {statistics.map((statistic) => (
          <Col key={statistic.key} xs={24} sm={12} xl={6}>
            <Statistic title={statistic.title} value={statistic.value} />
          </Col>
        ))}
      </Row>

      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: label('menu.reports.Overview', 'Overview'),
            children: overview,
          },
          {
            key: 'configuration',
            label: label(
              'pages.integrations.carat.tab_configuration',
              'Configuration',
            ),
            children: configuration,
          },
          {
            key: 'positions',
            label: `${label(
              'pages.integrations.carat.positions',
              'Positions',
            )} (${positionCount})`,
            children: <CaratPlanPositions details={details} format={format} />,
          },
          {
            key: 'pricing',
            label: label('pages.integrations.carat.tab_pricing', 'Pricing'),
            children: pricing,
          },
          {
            key: 'catalogs',
            label: label(
              'pages.integrations.carat.tab_catalogs',
              'Catalogues & delivery',
            ),
            children: catalogsTab,
          },
          {
            key: 'customer',
            label: label('entity.customer.singular', 'Customer'),
            children: customer,
          },
          {
            key: 'technical',
            label: label('pages.integrations.carat.tab_technical', 'Technical'),
            children: technical,
          },
        ]}
      />
    </Flex>
  );
};

/**
 * The Carat plannings of one project (CLICKUP-86cb5zayy), one collapsible panel
 * per quotation variation. A panel's detail is fetched the first time it is
 * opened, so a project with a dozen plannings still loads at list speed.
 */
const CaratPlanViewer: React.FC<CaratPlanViewerProps> = ({
  plannings,
  leadDetails,
  loading,
  selectedPlanning,
}) => {
  const format = useCaratFormat();

  if (loading) return <Skeleton active paragraph={{ rows: 4 }} />;

  if (plannings.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <F
            id="pages.integrations.carat.no_plannings"
            defaultMessage="No Carat planning has been received for this project yet."
          />
        }
      />
    );
  }

  const items = plannings.map((planning) => ({
    key: planning.id,
    label: (
      <Flex align="center" gap={8} wrap>
        <Text strong>{planning.commission_id}</Text>
        {planning.variation && (
          <Tag>
            <F
              id="pages.integrations.carat.variation"
              defaultMessage="Variation"
            />{' '}
            {planning.variation}
          </Tag>
        )}
        {planning.agent_name && (
          <Text type="secondary">{planning.agent_name}</Text>
        )}
      </Flex>
    ),
    extra: planning.deal_value ? (
      <Text type="secondary">{format.money(Number(planning.deal_value))}</Text>
    ) : null,
    children: (
      <CaratSinglePlanViewer planning={planning} leadDetails={leadDetails} />
    ),
  }));

  return (
    <Collapse
      className={styles.collapse}
      items={items}
      defaultActiveKey={[(selectedPlanning ?? plannings[0]).id]}
    />
  );
};

export default CaratPlanViewer;
